import { useGetWalletGems } from "@/hooks/api";
import { trackLegacyEvent } from "@analytics";
import { useUserActivities } from "@hooks/useUserActivities";
import {
  Diamond as DiamondIcon,
  LocalFireDepartment as FireIcon,
} from "@mui/icons-material";
import {
  Alert,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  Menu,
  Stack,
  Typography,
} from "@mui/material";
import type React from "react";
import { useState } from "react";
import { ActivityListItem } from "./ActivityListItem";
import { EmptyState } from "./EmptyState";

interface UserActivityProps {
  walletAddress: string | null;
  disabled?: boolean;
}

export const UserActivity: React.FC<UserActivityProps> = ({
  walletAddress,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const { activities, isLoading, error, unreadCount, markTopActivityAsRead } =
    useUserActivities(walletAddress);

  const { walletGems } = useGetWalletGems(walletAddress || null, false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    markTopActivityAsRead();

    trackLegacyEvent({
      userJourney: "Activity",
      screen: "Header",
      control: "Activities",
      controlType: "Button",
      action: "Pressed",
      extras: {
        path: window.location.pathname,
        activitiesCount: unreadCount,
      },
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton onClick={handleClick} disabled={disabled}>
        <Badge
          badgeContent={unreadCount}
          invisible={unreadCount === 0}
          max={99}
          color="success"
        >
          <FireIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          },
          paper: {
            sx: {
              overflow: "hidden",
            },
          },
        }}
      >
        <Box
          sx={{
            padding: "16px",
            borderBottom: "1px solid #444444",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" gap="8px">
              <Typography variant="h5">Your Gems History</Typography>
              {activities?.length > 0 && (
                <Chip
                  label={activities?.length}
                  size="small"
                  variant="filled"
                />
              )}
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            maxHeight: "40vh",
            overflow: "auto",
          }}
        >
          {error ? (
            <Box sx={{ padding: "16px" }}>
              <Alert
                severity="error"
                sx={{
                  backgroundColor: "#444444",
                  color: "#ffffff",
                  border: "1px solid #ff6b6b",
                }}
              >
                Failed to load activities. Please try again.
              </Alert>
            </Box>
          ) : isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "32px",
              }}
            >
              <CircularProgress size={24} sx={{ color: "#36E0F8" }} />
            </Box>
          ) : !activities?.length ? (
            <EmptyState />
          ) : (
            <List
              sx={{
                padding: 0,
              }}
            >
              {activities
                .filter((activity) => !!activity.quest_name)
                .map((activity, index) => (
                  <ActivityListItem
                    key={`${activity.quest_id}-${activity.occurred_at}-${index}`}
                    activity={activity}
                  />
                ))}
            </List>
          )}
        </Box>

        {activities?.length > 0 && (
          <>
            <Divider sx={{ borderColor: "#444444" }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                p: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption">Total</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <DiamondIcon sx={{ fontSize: 16 }} />
                  <Typography variant="body2" fontWeight={600}>
                    {walletGems?.gems ?? 0}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};
